"use client";

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function TransitionHero() {
  const [dataLines, setDataLines] = useState<string[]>([]);

  useEffect(() => {
    const lines = Array.from({ length: 60 }).map(
      (_, i) =>
        `0x${Math.random().toString(16).substring(2, 10).toUpperCase()} // ${i
          .toString(16)
          .padStart(3, "0")}`
    );
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDataLines(lines);
  }, []);

  return (
    <section className="relative w-full bg-[#02040a] overflow-hidden border-b border-white/5">
      
      {/* TOP / BOTTOM SCANNING LINES (updated colors) */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[rgba(6,182,212,0.6)] to-transparent animate-pulse" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[rgba(139,92,246,0.6)] to-transparent animate-pulse" />

      {/* DATA NOISE WALL (KineticBackground style alignment) */}
      <div className="absolute inset-0 z-0 opacity-[0.06] pointer-events-none font-mono text-[10px] flex flex-wrap gap-x-6 gap-y-3 p-4 overflow-hidden select-none">
        {dataLines.map((line, i) => (
          <span key={i} className="text-cyan-500 whitespace-nowrap">
            {line}
          </span>
        ))}
        <div className="absolute inset-0 bg-[url('/noise.svg')] opacity-20 mix-blend-overlay" />
      </div>

      {/* SIDE MARKERS (updated with dashed borders or subtle effects) */}
      <div className="absolute left-6 md:left-12 top-0 bottom-0 flex flex-col justify-center gap-10 z-20">
        <PlusIcon />
        <PlusIcon />
        <PlusIcon />
      </div>

      <div className="absolute right-6 md:right-12 top-0 bottom-0 flex flex-col justify-center gap-10 z-20">
        <PlusIcon />
        <PlusIcon />
        <PlusIcon />
      </div>

      {/* GLOW CORE (updated colors and blur) */}
      <div className="absolute inset-0 flex items-center justify-center z-0">
        <div className="w-[60vw] h-[14vw] bg-gradient-to-r from-cyan-500/20 via-violet-500/20 to-fuchsia-500/20 blur-[120px] rounded-full animate-pulse" />
      </div>

      {/* MAIN CONTENT */}
      <div className="relative z-10 py-24 md:py-32 px-8 flex flex-col items-center text-center">
        
        {/* MICRO STATUS BAR (aligned with HUD style) */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-6 flex items-center gap-4 font-mono text-[10px] tracking-[0.5em] text-cyan-400 uppercase"
        >
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-ping" />
            SYSTEM_READY
          </span>
          <span className="text-white/75">|</span>
          <span>ADAPTIVE_EXECUTION</span>
        </motion.div>

        {/* TITLE (small title above headline) */}
        <motion.h2
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-2xl md:text-5xl font-black tracking-tighter uppercase text-white mb-4"
        >
          CESTUS IS YOUR
        </motion.h2>

        {/* MAIN HEADLINE (replace with GlitchTitle for alignment) */}
        <GlitchTitle text="INTELLIGENCE ARCHITECT" />

        {/* DESCRIPTION (wrap in terminal-style box for alignment) */}
        <AnimatePresence mode="wait">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="relative mt-8 max-w-3xl p-6 border border-white/10 bg-white/[0.02] backdrop-blur-sm rounded-lg overflow-hidden"
          >
            {/* Decorative Corners */}
            <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-cyan-500" />
            <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-fuchsia-500" />
            <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-cyan-500" />
            <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-fuchsia-500" />

            {/* Scanline */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/[0.03] to-transparent bg-[length:100%_4px] pointer-events-none" />

            {/* Header */}
            <div className="flex justify-between items-center mb-4 border-b border-white/10 pb-2">
              <span className="font-mono text-xs text-fuchsia-400 tracking-widest">
                {'// SYS_DESC'}
              </span>
              <span className="flex items-center gap-2 font-mono text-[10px] text-white/75 uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                Live Feed
              </span>
            </div>

            {/* Body Text */}
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest leading-relaxed text-blue-100/80">
              Cestus orchestrates intelligence at scale.
              From learning to real-time execution, intelligence flows across
              on-demand compute without friction, vendor lock-in, or manual coordination.
            </p>
          </motion.div>
        </AnimatePresence>

        {/* TECH DIVIDER (updated colors) */}
        <div className="mt-12 w-full max-w-xl">
          <div className="relative h-px w-full bg-white/5">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-violet-500 to-fuchsia-500 opacity-40 blur-[2px]" />
          </div>

          <div className="mt-4 flex justify-between font-mono text-[10px] text-white/75 uppercase tracking-[0.25em]">
            <span>Mem_Alloc: 0x91AF</span>
            <span>Substrate: Active</span>
            <span>Auth: Secure</span>
          </div>
        </div>
      </div>

      {/* VIGNETTE + GRAIN (already aligned) */}
      <div className="absolute inset-0 pointer-events-none z-[50] bg-[radial-gradient(circle_at_center,transparent_20%,#02040a_100%)]" />
      <div className="absolute inset-0 pointer-events-none z-[60] opacity-[0.08] bg-[url('/noise.svg')]" />
    </section>
  );
}

function PlusIcon() {
  return (
    <div className="text-cyan-500/75 text-2xl font-thin leading-none select-none">
      +
    </div>
  );
}

// GlitchTitle from SystemFailure (copied and adapted)
function GlitchTitle({ text }: { text: string }) {
  return (
    <div className="relative w-full h-[14vw] md:h-[7vw] mb-2 md:mb-6">
      <AnimatePresence mode="wait">
        <motion.div
          key={text}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0"
        >
          {/* Layer 1: Ghost Stroke */}
          <h1
            className="absolute inset-0 text-[14vw] md:text-[7vw] font-black leading-none tracking-tighter text-transparent uppercase select-none"
            style={{ WebkitTextStroke: '1px rgba(255,255,255,0.15)' }}
          >
            {text}
          </h1>

          {/* Layer 2: Filled Text with Clip Reveal */}
          <motion.h1
            initial={{ clipPath: "inset(0 100% 0 0)" }}
            animate={{ clipPath: "inset(0 0% 0 0)" }}
            transition={{ duration: 0.5, ease: "circOut" }}
            className="absolute inset-0 text-[14vw] md:text-[7vw] font-black leading-none tracking-tighter text-white uppercase select-none z-10"
            style={{ textShadow: '0 0 30px rgba(6,182,212,0.5)' }}
          >
            {text}
          </motion.h1>

          {/* Layer 3: Glitch Clones */}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0, 1, 0], x: [-2, 2, -1, 0] }}
            transition={{ duration: 0.4, times: [0, 0.2, 0.4, 0.6, 1] }}
            className="absolute top-0 left-1 text-[14vw] md:text-[7vw] font-black leading-none tracking-tighter text-fuchsia-500/75 uppercase mix-blend-screen select-none"
          >
            {text}
          </motion.h1>
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0, 1, 0], x: [2, -2, 1, 0] }}
            transition={{ duration: 0.4, delay: 0.1, times: [0, 0.2, 0.4, 0.6, 1] }}
            className="absolute top-0 -left-1 text-[14vw] md:text-[7vw] font-black leading-none tracking-tighter text-cyan-500/75 uppercase mix-blend-screen select-none"
          >
            {text}
          </motion.h1>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}